/**
 * Shared scene UI helpers.
 *
 * Kills the 3 copies of "back button" + "HUD panel" + "countdown label" that
 * lived in FroggerScene, SnakeScene, and PipelineScene.
 *
 * Conventions (read from data/shared.config.js when a config is available):
 *   - back button: pill 220x60 @ (game.width - 130, 36), label 'common.back'
 *   - HUD panel:   sprite from skin.<game>.hud.panel placed at (10, 8)
 *   - countdown:   96px amber text centered on the canvas
 */

import { Container, Graphics, Text, TextStyle } from '../../vendor/pixi.min.mjs';
import { t, otherLang, toggleLang } from '../config/i18n.js';

// ── Colour utilities ───────────────────────────────────────────────
// Mini helpers for tinting the base chip colours up/down so the chip
// bevel ("dark outer rim → main fill → lighter top highlight") and the
// button hover/press states can all be derived from a single source
// colour without hard-coding three palette entries per element.

/**
 * Multiply each RGB channel by `factor` (0..1). Lower = darker.
 *
 * @param {number} hex 0xRRGGBB
 * @param {number} [factor=0.6]
 * @returns {number} 0xRRGGBB
 */
export function darken(hex, factor = 0.6) {
    const r = Math.max(0, Math.floor(((hex >> 16) & 0xff) * factor));
    const g = Math.max(0, Math.floor(((hex >> 8) & 0xff) * factor));
    const b = Math.max(0, Math.floor((hex & 0xff) * factor));
    return (r << 16) | (g << 8) | b;
}

/**
 * Add a flat `amount` (0..255) to each RGB channel. Higher = lighter.
 *
 * @param {number} hex 0xRRGGBB
 * @param {number} [amount=50]
 * @returns {number} 0xRRGGBB
 */
export function lighten(hex, amount = 50) {
    const r = Math.min(255, ((hex >> 16) & 0xff) + amount);
    const g = Math.min(255, ((hex >> 8) & 0xff) + amount);
    const b = Math.min(255, (hex & 0xff) + amount);
    return (r << 16) | (g << 8) | b;
}

/**
 * Paint a chunky "chip" rectangle with the v2 UI's bevel stack:
 *
 *   shadow drop  →  darker outer rim  →  main fill  →  faux gradient
 *   highlight on the top half  →  faded white inner stroke
 *
 * Returns the graphics container so callers can position other children
 * (icon circle, text) relative to its `(0, 0)` origin. The container's
 * visual size is `w × h` plus the drop shadow underneath — children
 * positioned inside `[0..w, 0..h]` land on the chip face.
 *
 * @param {{ w: number, h: number, r?: number, color: number,
 *          shadowDy?: number, shadowAlpha?: number,
 *          rimRatio?: number, hlAlpha?: number, strokeAlpha?: number }} opts
 * @returns {Container}
 */
export function paintChip(opts) {
    const {
        w, h,
        r = 18,
        color,
        shadowDy = 8,
        shadowAlpha = 0.42,
        rimRatio = 0.55,
        hlAlpha = 0.45,
        strokeAlpha = 0.5,
    } = opts;

    const out = new Container();
    const rim = darken(color, rimRatio);
    const hl = lighten(color, 55);

    // Inner-radius derivation: when callers pass `r === 0` (sharp
    // corners — see in-game top-header chips), every nested layer
    // also needs to be drawn with radius 0 so the chip reads as
    // truly square. The previous `Math.max(2, r - inset)` guard
    // was only meant to keep small positive radii from going
    // negative, not to force-round genuinely-square chips.
    const inset = 3;
    const innerR = r > 0 ? Math.max(2, r - inset) : 0;
    const hlR    = r > 0 ? Math.max(2, r - inset - 2) : 0;

    // Drop shadow — soft, large offset so the chip floats over the bg.
    // Callers can opt out by passing `shadowAlpha: 0`; we skip the
    // Graphics entirely in that case so the chip isn't paying for an
    // invisible draw call (Frogger's flat top-header / footer cards
    // do this).
    if (shadowAlpha > 0) {
        const shadow = new Graphics();
        shadow.roundRect(0, shadowDy, w, h, r);
        shadow.fill({ color: 0x000000, alpha: shadowAlpha });
        out.addChild(shadow);
    }

    // Outer dark rim — gives the chip its chunky bordered look.
    const outer = new Graphics();
    outer.roundRect(0, 0, w, h, r);
    outer.fill(rim);
    out.addChild(outer);

    // Inner main fill, inset by 3 px so 3 px of dark rim shows around it.
    const body = new Graphics();
    body.roundRect(inset, inset, w - inset * 2, h - inset * 2, innerR);
    body.fill(color);
    out.addChild(body);

    // Top half "highlight" — faux gradient via a faded lighter band.
    const hlH = (h - inset * 2) / 2;
    const highlight = new Graphics();
    highlight.roundRect(inset + 2, inset + 1, w - (inset + 2) * 2, hlH, hlR);
    highlight.fill({ color: hl, alpha: hlAlpha });
    out.addChild(highlight);

    // Faded white inner stroke — adds the bright glassy rim seen on the
    // mockup chips/buttons.
    const innerStroke = new Graphics();
    innerStroke.roundRect(inset, inset, w - inset * 2, h - inset * 2, innerR);
    innerStroke.stroke({ color: 0xffffff, width: 1.8, alpha: strokeAlpha });
    out.addChild(innerStroke);

    return out;
}

/**
 * Paint a chunky circular button (help / leaderboard / etc). Same bevel
 * recipe as {@link paintChip} but for circles.
 *
 * @param {{ size: number, color: number,
 *          shadowDy?: number, shadowAlpha?: number,
 *          rimRatio?: number, hlAlpha?: number, strokeAlpha?: number }} opts
 * @returns {Container} centred at (0,0)
 */
export function paintCircleButton(opts) {
    const {
        size, color,
        shadowDy = 6,
        shadowAlpha = 0.42,
        rimRatio = 0.55,
        hlAlpha = 0.5,
        strokeAlpha = 0.55,
    } = opts;

    const out = new Container();
    const rim = darken(color, rimRatio);
    const hl = lighten(color, 70);
    const radius = size / 2;

    const shadow = new Graphics();
    shadow.circle(0, shadowDy, radius);
    shadow.fill({ color: 0x000000, alpha: shadowAlpha });
    out.addChild(shadow);

    const outer = new Graphics();
    outer.circle(0, 0, radius);
    outer.fill(rim);
    out.addChild(outer);

    const inset = 3;
    const body = new Graphics();
    body.circle(0, 0, radius - inset);
    body.fill(color);
    out.addChild(body);

    // Top half highlight — clipped to a half-ellipse via a thick stroke.
    // (Simpler than a true mask: a faded ellipse offset up reads as an
    // upper-half glow without artefacts.)
    const highlight = new Graphics();
    highlight.ellipse(0, -radius * 0.32, radius - inset - 4, (radius - inset) * 0.55);
    highlight.fill({ color: hl, alpha: hlAlpha });
    out.addChild(highlight);

    const innerStroke = new Graphics();
    innerStroke.circle(0, 0, radius - inset);
    innerStroke.stroke({ color: 0xffffff, width: 1.8, alpha: strokeAlpha });
    out.addChild(innerStroke);

    return out;
}

// Chip-style "back to menu" pill — matches the rounded-rect cards in
// `SnakeTopBar` / `FroggerTopBar` (drop shadow, deep menu-purple stroke,
// Poppins typography) so the entire top bar reads as one cohesive strip.
// Purple body (`PÁLYA` chip colour) — semantically a navigation action,
// so it mirrors the board-progress chip.
//
// `BACK_RADIUS = 0` — per UX direction the in-game top-header buttons
// are sharp-cornered. The companion stat chips in `FroggerTopBar` /
// `SnakeTopBar` use the same value, so the strip stays visually unified.
export const BACK_W = 300;
const BACK_H = 70;
const BACK_RADIUS = 0;
const BACK_BG = 0xa855f7;
const BACK_STROKE = 0x3b1670;
const BACK_STROKE_W = 2.5;
const BACK_TEXT = '#ffffff';
const BACK_FONT = 'Poppins, Arial, sans-serif';

// Big "3-2-1" countdown card — matches the menu / top-bar chip aesthetic
// (deep menu-purple body, lighter purple accent stroke, drop shadow, chunky
// rounded corners) so the start-of-round prompt reads as part of the same
// design language. Sized for a single digit at full font; longer strings
// (e.g. "RAJT!" / "GO!") auto-shrink to fit the card width via the `.text`
// setter on the returned Container.
const COUNTDOWN_CARD = {
    W: 320,
    H: 320,
    // Sharp corners — UX direction is to drop rounded corners across
    // the in-game UI (buttons, popup modals, footer/header tiles), so
    // the pre-game 3-2-1 countdown card matches that flat aesthetic
    // instead of being the lone rounded element on screen.
    RADIUS: 0,
    BG_COLOR: 0x3b1670,
    STROKE_COLOR: 0xa855f7,
    STROKE_W: 4,
    SHADOW_COLOR: 0x000000,
    SHADOW_ALPHA: 0.35,
    SHADOW_DY: 10,
    TEXT_PAD: 32, // px of breathing room inside the card on each side
};

const COUNTDOWN_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial, sans-serif',
    fontSize: 220,
    fontWeight: '900',
    fill: '#fbbf24',
    align: 'center',
    letterSpacing: 2,
    dropShadow: { color: '#000000', distance: 6, blur: 8, alpha: 0.55, angle: Math.PI / 4 },
});

/**
 * Builds the standard "back to menu" pill button.
 *
 * @param {{ width: number, height: number, sceneManager: any }} game
 * @param {() => void} [onClick] custom handler (defaults to switchScene('menu'))
 * @returns {Container} positioned at top-right, anchored at its centre
 */
export function createBackButton(game, onClick) {
    const btn = new Container();
    btn.eventMode = 'static';
    btn.cursor = 'pointer';

    // Paint the chip then centre it inside the container so the existing
    // (game.width - BACK_W/2 - 20, 55) anchor still lines up visually.
    // `shadowAlpha: 0` drops the drop-shadow per UX direction so the
    // square back pill reads as a flat ribbon, matching the rest of
    // the (shadow-less) sharp-cornered top header across all games.
    const chip = paintChip({ w: BACK_W, h: BACK_H, r: BACK_RADIUS, color: BACK_BG, shadowAlpha: 0 });
    chip.x = -BACK_W / 2;
    chip.y = -BACK_H / 2;
    btn.addChild(chip);

    const label = new Text({
        text: t('common.back'),
        style: new TextStyle({
            fontFamily: BACK_FONT,
            fontSize: 22,
            fontWeight: '900',
            fill: BACK_TEXT,
            letterSpacing: 1.2,
            dropShadow: { color: '#1d0a3a', alpha: 0.55, distance: 1, blur: 2 },
        }),
    });
    label.anchor.set(0.5);
    btn.addChild(label);

    btn.x = game.width - BACK_W / 2 - 20;
    btn.y = 55;

    const handler = onClick || (() => game.sceneManager.switchScene('menu'));
    btn.on('pointertap', handler);
    // Subtle hover/press feedback — keeps the bevel-stack visually anchored
    // while making the button feel tappable.
    btn.on('pointerover', () => { btn.scale.set(1.03); });
    btn.on('pointerout',  () => { btn.scale.set(1.0);  });
    btn.on('pointerdown', () => { btn.scale.set(0.97); });
    btn.on('pointerup',   () => { btn.scale.set(1.03); });
    return btn;
}

// In-game HU/EN language pill. Mirrors the menu's language toggle but
// styled to match the sharp-cornered purple back pill so the in-game
// top header reads as one cohesive strip. Square corners + flat (no
// shadow) for the same reason as `createBackButton`.
export const LANG_W = 96;
export const LANG_GAP = 16; // px between the language pill and the chrome cluster

/**
 * Builds the in-game HU/EN toggle pill. Tapping it flips the locale,
 * persists the choice, then restarts the current scene so every `t()`
 * label re-renders in the new language.
 *
 * Positioned just LEFT of the back pill by default; pass `opts.x` /
 * `opts.y` to override (e.g. when a game's edge HUD needs clearance).
 *
 * @param {{ width: number, sceneManager: any, theme?: any }} game
 * @param {{ x?: number, y?: number }} [opts]
 * @returns {Container} positioned at top-right, anchored at its centre
 */
export function createLangButton(game, opts = {}) {
    const btn = new Container();
    btn.eventMode = 'static';
    btn.cursor = 'pointer';

    const chip = paintChip({ w: LANG_W, h: BACK_H, r: BACK_RADIUS, color: BACK_BG, shadowAlpha: 0 });
    chip.x = -LANG_W / 2;
    chip.y = -BACK_H / 2;
    btn.addChild(chip);

    const label = new Text({
        // Show the language you can switch TO (on HU show "EN", vice versa).
        text: otherLang().toUpperCase(),
        style: new TextStyle({
            fontFamily: BACK_FONT,
            fontSize: 24,
            fontWeight: '900',
            fill: BACK_TEXT,
            letterSpacing: 1.5,
            dropShadow: { color: '#1d0a3a', alpha: 0.55, distance: 1, blur: 2 },
        }),
    });
    label.anchor.set(0.5);
    btn.addChild(label);

    // Default anchor: sit to the left of the back pill. The back pill's
    // left edge is at (game.width - 20 - BACK_W); leave a small gap.
    const backLeftEdge = game.width - 20 - BACK_W;
    btn.x = opts.x ?? (backLeftEdge - LANG_GAP - LANG_W / 2);
    btn.y = opts.y ?? 55;

    btn.on('pointertap', () => {
        const newLang = toggleLang();
        game.theme?.setLang?.(newLang);
        const current = game.sceneManager?.activeSceneName;
        if (current) game.sceneManager.switchScene(current);
    });
    btn.on('pointerover', () => { btn.scale.set(1.03); });
    btn.on('pointerout',  () => { btn.scale.set(1.0);  });
    btn.on('pointerdown', () => { btn.scale.set(0.97); });
    btn.on('pointerup',   () => { btn.scale.set(1.03); });
    return btn;
}

/**
 * Builds the HUD panel sprite for a given game.
 * Returns null when no skin sprite is configured (caller draws its own panel).
 *
 * @param {*} game
 * @param {string} gameId 'frogger' | 'snake' | 'pipeline'
 * @param {number} width
 * @param {number} height
 * @returns {import('../../vendor/pixi.min.mjs').Sprite | null}
 */
export function createHudPanel(game, gameId, width, height) {
    const sprite = game.skin?.createSprite(`${gameId}.hud.panel`, width, height, {
        anchorX: 0,
        anchorY: 0,
        fitMode: 'cover',
    });
    if (!sprite) return null;
    sprite.x = 10;
    sprite.y = 8;
    return sprite;
}

/**
 * Builds the giant centred countdown card (purple card + big yellow
 * number, drop shadow). Returns a {@link Container} that exposes the
 * same `.text` and `.visible` interface as the old plain `Text` node,
 * so existing callers (`SnakeScene` / `PipelineScene`) keep working
 * without changes.
 *
 * @param {{ width: number, height: number }} game
 * @param {{ initial?: string, visible?: boolean }} [opts]
 * @returns {Container}
 */
export function createCountdownLabel(game, opts = {}) {
    const W = COUNTDOWN_CARD.W;
    const H = COUNTDOWN_CARD.H;
    const R = COUNTDOWN_CARD.RADIUS;

    const container = new Container();
    container.x = game.width / 2;
    container.y = game.height / 2;
    container.visible = !!opts.visible;

    const shadow = new Graphics();
    shadow.roundRect(-W / 2, -H / 2 + COUNTDOWN_CARD.SHADOW_DY, W, H, R);
    shadow.fill({ color: COUNTDOWN_CARD.SHADOW_COLOR, alpha: COUNTDOWN_CARD.SHADOW_ALPHA });
    container.addChild(shadow);

    const card = new Graphics();
    card.roundRect(-W / 2, -H / 2, W, H, R);
    card.fill(COUNTDOWN_CARD.BG_COLOR);
    card.stroke({ color: COUNTDOWN_CARD.STROKE_COLOR, width: COUNTDOWN_CARD.STROKE_W });
    container.addChild(card);

    const text = new Text({ text: opts.initial ?? '', style: COUNTDOWN_STYLE });
    text.anchor.set(0.5);
    text.x = 0;
    text.y = 6; // tiny nudge — looks more centred with descenders / drop shadow
    container.addChild(text);

    // Auto-shrink any text wider than the card. Single digits stay at the
    // full 220 px font; longer strings ("RAJT!" / "GO!") scale down to fit.
    const refit = () => {
        text.scale.set(1, 1);
        const maxW = W - COUNTDOWN_CARD.TEXT_PAD * 2;
        const maxH = H - COUNTDOWN_CARD.TEXT_PAD * 2;
        if (text.width <= maxW && text.height <= maxH) return;
        const s = Math.min(maxW / text.width, maxH / text.height);
        text.scale.set(s, s);
    };
    refit();

    // Proxy `.text` so callers keep using `label.text = '3'` like they did
    // when this helper returned a bare Text node.
    Object.defineProperty(container, 'text', {
        configurable: true,
        get: () => text.text,
        set: (v) => { text.text = v; refit(); },
    });

    return container;
}
